import React, { useState } from "react";
import { Box, IconButton, Tooltip } from "@mui/material";
import { AccessTime, CalendarMonth, Edit } from "@mui/icons-material";
import SwapHorizOutlinedIcon from "@mui/icons-material/SwapHorizOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import moment from "moment";
import { useSelector } from "react-redux";
import { GlobalSidePanel } from "components/DesignSystem/GlobalSidePanel";
import { RequestDetailHeader } from "components/DesignSystem/RequestDetailHeader";
import { RequestStatusTimeline } from "components/DesignSystem/RequestStatusTimeline";
import { RequestCancelButton } from "components/DesignSystem/RequestCancelButton";
import RequestDetailsSection from "components/DesignSystem/RequestDetailsSection";
import { detailAccentPalette } from "components/DesignSystem/RequestDetailField";
import { ConfirmationDialog } from "components/CustomComponents/ConfirmationDialog";
import { customAPI } from "services/utils/CustomAPI";
import { showToast } from "services/utils/Status";
import { CorrectionApplyPanel } from "../CorrectionShared/CorrectionApplyPanel";
import { formatShiftDateTime } from "./shiftCorrectionHelpers";
import { designColors } from "services/constants/designTokens";

export const ViewShiftChange = ({
  open = true,
  selectedData,
  onClose,
  onUpdated,
  checkAccess,
}) => {
  const requestCompanyId = useSelector((state) => state.tokenReducer.topbarCompanyId);
  const { domain, userDetails } = useSelector((state) => state.tokenReducer);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  // Edit and Cancel used to be gated on status alone, so a view-only user
  // could open either from this panel with no permission check at all (the
  // New Request button on ShiftCorrection/index.js already gates creation
  // on this same checkAccess). Both now require it too, hiding rather than
  // disabling the action for a viewer who can't use it.
  const canActOn = checkAccess && selectedData?.status === "Pending";
  const employeeName =
    selectedData?.employee_name ||
    selectedData?.user_name ||
    selectedData?.username ||
    userDetails?.first_name ||
    "N/A";
  const employeeId =
    selectedData?.employee_id ||
    selectedData?.emp_id ||
    userDetails?.employee_id ||
    "-";
  const department =
    selectedData?.department_name ||
    selectedData?.department ||
    userDetails?.department_name ||
    "-";
  const designation =
    selectedData?.designation_name ||
    selectedData?.designation ||
    userDetails?.designation ||
    "-";
  const isWeekOffSwap = selectedData?.request_type === "week_off_swap";
  const requestTitle = isWeekOffSwap
    ? "Week Off Swap Request"
    : "Schedule Change Request";
  const dateValue = isWeekOffSwap
    ? `${selectedData?.date ? moment(selectedData.date).format("DD MMM YYYY") : "-"} <-> ${
        selectedData?.swap_date ? moment(selectedData.swap_date).format("DD MMM YYYY") : "-"
      }`
    : selectedData?.date
      ? moment(selectedData.date).format("DD MMM YYYY")
      : "-";
  const statusFlowData = {
    ...selectedData,
    applied_by:
      selectedData?.applied_by ||
      selectedData?.createdby ||
      selectedData?.requested_by ||
      employeeName,
    applied_on: formatShiftDateTime(
      selectedData?.applied_on ||
        selectedData?.createdat ||
        selectedData?.created_at ||
        selectedData?.created_date,
    ),
    // selectedData?.approver_name is the assigned approver, stamped at
    // apply time before anyone has acted -- it is not proof of approval
    // and must not be preferred over (or substitute for) the backend's
    // status-derived approved_by.
    approved_by: selectedData?.approved_by,
    approved_at: formatShiftDateTime(
      selectedData?.approved_at || selectedData?.approved_on,
    ),
    rejected_by: selectedData?.rejected_by,
    rejected_at: formatShiftDateTime(
      selectedData?.rejected_at || selectedData?.rejected_on,
    ),
    cancelled_by: selectedData?.cancelled_by || selectedData?.canceled_by,
    cancelled_at: formatShiftDateTime(
      selectedData?.cancelled_at ||
        selectedData?.cancelled_on ||
        selectedData?.canceled_at ||
        selectedData?.canceled_on,
    ),
    cancelled_reason:
      selectedData?.cancelled_reason ||
      selectedData?.cancel_reason ||
      selectedData?.canceled_reason,
  };

  const handleCancelRequest = async () => {
    setCancelling(true);
    const response = await customAPI(
      `${domain}my_zone/apply/shift_change/myzone-shift-change/?company_id=${requestCompanyId}`,
      "PUT",
      { id: selectedData?.id, approval_status: "Cancelled" },
    );
    setCancelling(false);
    setShowCancelDialog(false);
    if (response?.ok) {
      showToast("success", response?.responseDetails?.message || "Request cancelled");
      onUpdated?.();
      onClose();
    } else {
      showToast("error", response?.message);
    }
  };

  const editAction = canActOn ? (
    <Tooltip title="Edit" arrow>
      <IconButton onClick={() => setIsEditOpen(true)}>
        <Edit />
      </IconButton>
    </Tooltip>
  ) : null;

  return (
    <GlobalSidePanel
      open={open}
      onClose={onClose}
      title={requestTitle}
      subtitle="Request details and status"
      headerActions={editAction}
      bodySx={{ backgroundColor: designColors.pageBg }}
      footer={
        canActOn ? (
          <RequestCancelButton onClick={() => setShowCancelDialog(true)} />
        ) : null
      }
    >
      <ConfirmationDialog
        open={showCancelDialog}
        onClose={() => setShowCancelDialog(false)}
        onConfirm={handleCancelRequest}
        title="Cancel Request"
        message="Are you sure you want to cancel this request?"
        confirmText="Confirm"
        cancelText="Cancel"
        disableAction={cancelling}
      />
      {isEditOpen ? (
        <CorrectionApplyPanel
          open={isEditOpen}
          onClose={() => setIsEditOpen(false)}
          onCreate={() => {
            onUpdated?.();
            onClose();
          }}
          title={requestTitle}
          subtitle="Update the schedule change request."
          defaultCorrectionType="wrong_shift"
          mode="shift-change"
          editRecord={selectedData}
        />
      ) : null}
      <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 2 }}>
        <RequestDetailHeader
          userDetails={{
            image_url: selectedData?.image_url || userDetails?.image_url,
            employee_id: employeeId,
            user_name: employeeName,
            department_name: department,
            designation,
          }}
          status={selectedData?.status}
          colorCode={selectedData?.color_code}
          variant="employeeDetailsCard"
        />
        <RequestDetailsSection
          fields={[
            { icon: CalendarMonth, label: isWeekOffSwap ? "Dates" : "Date", value: dateValue, accent: detailAccentPalette.indigo },
            { icon: AccessTime, label: "Applied On", value: statusFlowData.applied_on || "-", accent: detailAccentPalette.blue },
            { icon: SwapHorizOutlinedIcon, label: isWeekOffSwap ? "Original Schedule" : "Original Shift", value: selectedData?.original_shift || "-", accent: detailAccentPalette.pink },
            { icon: SwapHorizOutlinedIcon, label: isWeekOffSwap ? "After Swap" : "New Shift", value: selectedData?.corrected_shift || "-", accent: detailAccentPalette.blue },
            { icon: DescriptionOutlinedIcon, label: "Reason", value: selectedData?.reason || "-", accent: detailAccentPalette.orange, multiline: true },
          ]}
        />

        <RequestStatusTimeline endDate={[selectedData?.date, selectedData?.swap_date]} data={statusFlowData} hidePendingStep />

      </Box>
    </GlobalSidePanel>
  );
};

export default ViewShiftChange;
